import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import HandymanIcon from '@mui/icons-material/Handyman';
import RoofingIcon from '@mui/icons-material/Roofing';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import GrassIcon from '@mui/icons-material/Grass';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { portfolioFilters, portfolioSites } from '../data/portfolio';
import type { NicheId } from '../types';
type FilterId = NicheId | 'all';
const filterIcons: Record<FilterId, React.ElementType> = {
  all: GridViewRoundedIcon,
  contractors: HandymanIcon,
  roofing: RoofingIcon,
  business: RocketLaunchIcon,
  dental: MedicalServicesIcon,
  restaurant: RestaurantIcon,
  landscaping: GrassIcon,
  beauty: ContentCutIcon
};
interface CategoryTilesProps {
  value: FilterId;
  onChange: (value: FilterId) => void;
  ariaLabel: string;
}
export function CategoryTiles({
  value,
  onChange,
  ariaLabel
}: CategoryTilesProps) {
  const counts = useMemo(() => {
    const result: Partial<Record<FilterId, number>> = {
      all: portfolioSites.length
    };
    portfolioSites.forEach(site => {
      result[site.niche] = (result[site.niche] ?? 0) + 1;
    });
    return result;
  }, []);
  return <div role="tablist" aria-label={ariaLabel} className="grid grid-cols-2 gap-3 [perspective:1200px] sm:grid-cols-4 xl:grid-cols-8" data-id="element-23" data-mp="26cl12iq728k6">
      {portfolioFilters.map((filter, index) => {
      const active = filter.id === value;
      const Icon = filterIcons[filter.id];
      const count = counts[filter.id] ?? 0;
      return <motion.button key={filter.id} type="button" role="tab" aria-selected={active} onClick={() => onChange(filter.id)} initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.45,
        delay: index * 0.05,
        ease: [0.16, 1, 0.3, 1]
      }} whileHover={{
        y: -9,
        rotateX: 8,
        rotateY: -6,
        scale: 1.05
      }} whileTap={{
        scale: 0.98
      }} style={{
        transformPerspective: 900
      }} className={`group relative flex flex-col items-start gap-0.5 rounded-[18px] border-2 px-3 pb-3 pt-3.5 text-left transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 ${active ? 'border-transparent bg-brand-blue shadow-[0_14px_30px_rgba(27,77,204,0.4)]' : 'border-line bg-white shadow-card hover:border-brand-blue hover:shadow-glow-blue'}`} data-id="element-24" data-mp="1nl234qb11kp0" data-mpi={index}>
            {active ? <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[18px]" data-id="element-25" data-mp="2ffqgd3ztnu0o">
                <span className="absolute left-1/2 top-1/2 h-[320%] w-[320%] -translate-x-1/2 -translate-y-1/2" data-id="element-26" data-mp="3v4g67huo5e9i">
                  <span className="block h-full w-full animate-spin-border bg-[conic-gradient(from_0deg,#FFD500_0deg,rgba(255,213,0,0)_110deg,rgba(255,213,0,0)_250deg,#FFD500_360deg)]" data-id="element-27" data-mp="dnycgairq6gm" />
                </span>
                <span className="absolute inset-[2px] rounded-[16px] bg-brand-blue" data-id="element-28" data-mp="3h7jab1zp16lr" />
              </span> : null}

            <span className={`relative z-10 flex h-[22px] w-[22px] items-center justify-center self-end rounded-full transition-colors duration-300 ${active ? 'bg-white/20 text-white' : 'bg-brand-blue/[0.08] text-brand-blue'}`} data-id="element-29" data-mp="3r3cv18nkg6em">
              <Icon style={{
            fontSize: 13
          }} data-id="element-30" data-mp="11f3w70rtxiw7" />
            </span>

            <span className={`relative z-10 font-display text-[28px] font-extrabold leading-none transition-colors duration-300 ${active ? 'text-brand-yellow' : 'text-brand-blue'}`} data-id="element-31" data-mp="372l0gqjqn3jj">
              {count}
            </span>
            <span className={`relative z-10 mt-0.5 text-[11.5px] font-semibold uppercase tracking-wide transition-colors duration-300 ${active ? 'text-white/90' : 'text-muted'}`} data-id="element-32" data-mp="3g5domkxmiz55">
              {filter.label}
            </span>
          </motion.button>;
    })}
    </div>;
}